/**
 * Admin.tsx — Gestão de usuários e reindex manual (apenas Natan/admin)
 */

import { useState, useEffect } from 'react'
import { collection, getDocs, addDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore'
import JSZip from 'jszip'
import { db } from '../lib/firebase'
import { useAuth } from '../lib/auth'

interface KbUser {
  id: string
  email: string
  display_name: string
  role: 'admin' | 'member'
  added_by: string
  created_at: { toDate: () => Date } | null
}

export function Admin() {
  const { user } = useAuth()
  const [users, setUsers] = useState<KbUser[]>([])
  const [loading, setLoading] = useState(true)
  const [newEmail, setNewEmail] = useState('')
  const [newName, setNewName] = useState('')
  const [newRole, setNewRole] = useState<'admin' | 'member'>('member')
  const [adding, setAdding] = useState(false)
  const [reindexStatus, setReindexStatus] = useState<string | null>(null)
  const [exportStatus, setExportStatus] = useState<string | null>(null)
  const [exporting, setExporting] = useState(false)

  useEffect(() => {
    loadUsers()
  }, [])

  async function loadUsers() {
    setLoading(true)
    try {
      const snap = await getDocs(collection(db, 'kb_users'))
      setUsers(
        snap.docs.map((d) => ({ id: d.id, ...d.data() } as KbUser))
      )
    } catch (err) {
      console.error(err)
    } finally {
      setLoading(false)
    }
  }

  async function addUser() {
    if (!newEmail.trim() || !user) return
    setAdding(true)
    try {
      // NOTA: O UID real do Firebase Auth não é conhecido aqui sem a Admin SDK.
      // Fluxo recomendado: Natan cria o usuário no console Firebase Auth,
      // obtém o UID, e adiciona aqui. Este form é um helper de UI.
      // Em produção, use uma Cloud Function com Admin SDK para criar usuário + doc.
      await addDoc(collection(db, 'kb_users'), {
        email: newEmail.trim(),
        display_name: newName.trim() || newEmail.trim(),
        role: newRole,
        added_by: user.uid,
        created_at: serverTimestamp(),
        // uid precisa ser preenchido manualmente após criar no Firebase Auth
        _pending_uid: true,
      })
      setNewEmail('')
      setNewName('')
      await loadUsers()
    } catch (err) {
      console.error(err)
    } finally {
      setAdding(false)
    }
  }

  async function removeUser(userId: string) {
    if (!confirm('Remover acesso deste usuário?')) return
    await deleteDoc(doc(db, 'kb_users', userId))
    await loadUsers()
  }

  async function triggerReindex() {
    setReindexStatus('Solicitando reindex...')
    try {
      const response = await fetch('/api/reindex', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      })
      const data = await response.json()
      setReindexStatus(data.message ?? 'Reindex solicitado.')
    } catch (err) {
      setReindexStatus(`Erro: ${err instanceof Error ? err.message : 'desconhecido'}`)
    }
    setTimeout(() => setReindexStatus(null), 5000)
  }

  /**
   * Baixa o vault inteiro como .zip preservando a estrutura de pastas
   * original do Obsidian (campo `path` em vault_notes).
   */
  async function downloadVaultZip() {
    setExporting(true)
    setExportStatus('Carregando notas...')
    try {
      const snap = await getDocs(collection(db, 'vault_notes'))
      setExportStatus(`Empacotando ${snap.size} notas...`)

      const zip = new JSZip()
      const root = zip.folder('vault')!

      let added = 0
      for (const d of snap.docs) {
        const data = d.data() as { path?: string; content_md?: string; title?: string }
        const relPath = (data.path && typeof data.path === 'string')
          ? data.path
          : `_orphans/${data.title || d.id}.md`
        const content = data.content_md ?? ''
        // sanitiza barras invertidas e leading slash
        const safePath = relPath.replace(/\\/g, '/').replace(/^\/+/, '')
        root.file(safePath, content)
        added++
      }

      setExportStatus(`Compactando ${added} arquivos...`)
      const blob = await zip.generateAsync({
        type: 'blob',
        compression: 'DEFLATE',
        compressionOptions: { level: 6 },
      })

      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      const ts = new Date().toISOString().slice(0, 10)
      a.href = url
      a.download = `vault-bam-${ts}.zip`
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)

      setExportStatus(`✓ ${added} notas baixadas (${(blob.size / 1024 / 1024).toFixed(2)} MB)`)
      setTimeout(() => setExportStatus(null), 6000)
    } catch (err) {
      setExportStatus(`Erro: ${err instanceof Error ? err.message : 'desconhecido'}`)
      setTimeout(() => setExportStatus(null), 8000)
    } finally {
      setExporting(false)
    }
  }

  return (
    <div
      style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '32px 24px',
        height: '100%',
        overflowY: 'auto',
      }}
    >
      <h1 style={{ fontSize: '20px', color: '#e8e8f0', marginBottom: '8px' }}>Admin</h1>
      <p style={{ fontSize: '13px', color: '#5a5a80', marginBottom: '32px' }}>
        Gestão de acesso e manutenção do índice.
      </p>

      {/* Seção: Usuários */}
      <section style={{ marginBottom: '40px' }}>
        <h2 style={{ fontSize: '15px', color: '#57f5c3', marginBottom: '16px' }}>
          Usuários com acesso
        </h2>

        {loading ? (
          <div style={{ color: '#5a5a80', fontSize: '13px' }}>Carregando...</div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', marginBottom: '20px' }}>
            <thead>
              <tr>
                {['Email', 'Nome', 'Role', 'Ação'].map((h) => (
                  <th
                    key={h}
                    style={{
                      textAlign: 'left',
                      padding: '8px 12px',
                      fontSize: '11px',
                      color: '#5a5a80',
                      borderBottom: '1px solid rgba(255,255,255,0.06)',
                    }}
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.id}>
                  <td style={tdStyle}>{u.email}</td>
                  <td style={tdStyle}>{u.display_name}</td>
                  <td style={tdStyle}>
                    <span
                      style={{
                        fontSize: '10px',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        background:
                          u.role === 'admin'
                            ? 'rgba(87, 245, 195, 0.15)'
                            : 'rgba(255,255,255,0.06)',
                        color: u.role === 'admin' ? '#57f5c3' : '#9090b0',
                      }}
                    >
                      {u.role}
                    </span>
                  </td>
                  <td style={tdStyle}>
                    <button
                      onClick={() => removeUser(u.id)}
                      className="btn"
                      style={{ padding: '3px 8px', fontSize: '11px', color: '#E91E63' }}
                    >
                      remover
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        {/* Adicionar usuário */}
        <div
          style={{
            padding: '16px',
            background: 'rgba(255,255,255,0.02)',
            border: '1px solid rgba(255,255,255,0.06)',
            borderRadius: '8px',
            display: 'flex',
            gap: '8px',
            flexWrap: 'wrap',
          }}
        >
          <input
            value={newEmail}
            onChange={(e) => setNewEmail(e.target.value)}
            placeholder="email@exemplo.com"
            style={{ flex: '1 1 200px' }}
          />
          <input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="Nome (opcional)"
            style={{ flex: '1 1 160px' }}
          />
          <select
            value={newRole}
            onChange={(e) => setNewRole(e.target.value as 'admin' | 'member')}
            style={{
              background: '#1a1a35',
              border: '1px solid rgba(255,255,255,0.06)',
              borderRadius: '6px',
              color: '#e8e8f0',
              padding: '8px 12px',
              fontSize: '13px',
            }}
          >
            <option value="member">member</option>
            <option value="admin">admin</option>
          </select>
          <button
            onClick={addUser}
            disabled={adding || !newEmail.trim()}
            className="btn btn-primary"
          >
            {adding ? 'Adicionando...' : 'Adicionar'}
          </button>
        </div>
        <p style={{ fontSize: '11px', color: '#5a5a80', marginTop: '8px' }}>
          Nota: O UID do Firebase Auth precisa ser associado manualmente ao doc após criar o usuário no console.
        </p>
      </section>

      {/* Seção: Download do Vault */}
      <section style={{ marginBottom: '40px' }}>
        <h2 style={{ fontSize: '15px', color: '#57f5c3', marginBottom: '8px' }}>
          Baixar vault completo
        </h2>
        <p style={{ fontSize: '13px', color: '#9090b0', marginBottom: '16px' }}>
          Gera um .zip com todas as notas do Firestore preservando a estrutura
          de pastas original do Obsidian (campo <code>path</code>).
        </p>
        <button
          onClick={downloadVaultZip}
          disabled={exporting}
          className="btn btn-primary"
        >
          {exporting ? 'Gerando .zip...' : 'Baixar vault (.zip)'}
        </button>
        {exportStatus && (
          <div
            style={{
              marginTop: '12px',
              fontSize: '12px',
              color: '#57f5c3',
              padding: '8px 12px',
              background: 'rgba(87, 245, 195, 0.08)',
              borderRadius: '6px',
            }}
          >
            {exportStatus}
          </div>
        )}
      </section>

      {/* Seção: Reindex */}
      <section>
        <h2 style={{ fontSize: '15px', color: '#57f5c3', marginBottom: '8px' }}>
          Reindexar Vault
        </h2>
        <p style={{ fontSize: '13px', color: '#9090b0', marginBottom: '16px' }}>
          Dispara a Cloud Function de reindex. Use após atualizar o vault local e rodar o script de ingestão.
        </p>
        <button onClick={triggerReindex} className="btn btn-primary">
          Solicitar Reindex
        </button>
        {reindexStatus && (
          <div
            style={{
              marginTop: '12px',
              fontSize: '12px',
              color: '#57f5c3',
              padding: '8px 12px',
              background: 'rgba(87, 245, 195, 0.08)',
              borderRadius: '6px',
            }}
          >
            {reindexStatus}
          </div>
        )}
      </section>
    </div>
  )
}

const tdStyle: React.CSSProperties = {
  padding: '8px 12px',
  fontSize: '13px',
  color: '#e8e8f0',
  borderBottom: '1px solid rgba(255,255,255,0.04)',
}
